/**
 * @file Contains the Tooltip component which is used to show information for and allows interation with a currently hovered over or selected EBAB dot.
 */
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import {
    ClickAwayListener,
    Divider,
    MenuList,
    Typography
} from '@mui/material';
import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import TooltipBox from './TooltipBox';
import { VsPeerGroupsMenuItem } from '../menus/dotCTAs/VsPeerGroupsMenuItem';
import { MoreDetailsMenuItem } from '../menus/dotCTAs/MoreDetailsMenuItem';
import { BenchmarkName } from '../text/BenchmarkName';

/**
 * Renders a MUI ClickAwayListener wrapper component if the isClickAway condition passes.
 * @component
 * @param {ReactNode} children - children to wrap
 * @param {Boolean} isClickAway - Whether to wrap children in ClickAwayListener
 * @param {Function} onClickAway - Function called when clicked away
 * @returns {React.ReactElement}
 */
const MaybeClickAwayListener = ({ children, isClickAway, onClickAway }) =>
    isClickAway ? (
        <ClickAwayListener onClickAway={onClickAway}>
            {children}
        </ClickAwayListener>
    ) : (
        children
    );

MaybeClickAwayListener.propTypes = {
    children: PropTypes.node,
    isClickAway: PropTypes.bool,
    onClickAway: PropTypes.func
};

/**
 * Renders a EBAB diagram tooltip which shows when a dot is hovered over or clicked.
 * This tooltip shows the portfolio long name and baScore, as well as a filter link which
 * allows the user to set the peer filters for the associated portfolio attributes (style, geoCategory and market cap)
 *
 * @component
 * @param {Object} position - The absolute x and y screen coordinates where the tooltip is to be rendered
 * @param {Object} dotData - Object containing the dot's data (e.g score, name, filter attributes)
 * @param {Function} onMoreDetailsClick - Function to handle More Details being clicked
 * @param {Boolean} isMoreDetailsDisabled - Boolean to define whether the `More Details` button is disabled, defaults to false
 * @param {Boolean} isSticky - If tooltip is to be fixed to viewport
 * @param {Function} handleClose - function to close sticky tooltip
 * @param {Function} onVsPeerGroupClick - function called when Vs Peer Group clicked in tooltip
 * @returns {React.ReactElement} - The rendered Tooltip component
 */
const FrontierTooltip = ({
    position,
    dotData,
    onMoreDetailsClick,
    isMoreDetailsDisabled = false,
    isSticky,
    handleClose,
    onVsPeerGroupClick
}) => {
    const { isAuthenticated } = useAuth0();

    return (
        <MaybeClickAwayListener
            onClickAway={handleClose}
            isClickAway={isSticky}
        >
            <TooltipBox
                role='dotTooltip'
                data-analyticsid='dot-tooltip'
                sx={{
                    top: position.y,
                    left: position.x,
                    zIndex: 100, //render on top of sticky pro header
                    width: 270
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    <Typography
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {dotData.portfolioLongName}
                    </Typography>
                    <BenchmarkName
                        portfolioData={dotData}
                        typographyProps={{
                            sx: {
                                fontSize: '0.8rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }
                        }}
                    />
                    <Typography
                        sx={{ fontSize: '0.85rem', paddingTop: 0.7 }}
                        data-analyticsid='dot-tooltip-header'
                    >
                        Behavioral Alpha Score | {dotData.baScore?.toFixed(2)}
                    </Typography>
                </Box>
                <Divider variant={'middle'} />
                <MenuList sx={{ paddingTop: 0.8, paddingBottom: 0.8 }}>
                    <VsPeerGroupsMenuItem
                        onClick={onVsPeerGroupClick}
                        disabled={!isAuthenticated}
                    />
                    <MoreDetailsMenuItem
                        onClick={(event) => {
                            handleClose(event);
                            onMoreDetailsClick();
                        }}
                        disabled={!isAuthenticated || isMoreDetailsDisabled}
                    />
                </MenuList>
            </TooltipBox>
        </MaybeClickAwayListener>
    );
};

FrontierTooltip.propTypes = {
    handleClose: PropTypes.func.isRequired,
    isSticky: PropTypes.bool,
    position: PropTypes.object.isRequired,
    dotData: PropTypes.object.isRequired,
    onMoreDetailsClick: PropTypes.func,
    isMoreDetailsDisabled: PropTypes.bool,
    onVsPeerGroupClick: PropTypes.func
};
export default FrontierTooltip;
